/**
 * PersonaCardPreview — Live editorial preview of the persona being built.
 * No card chrome. Open layout on the panel background. Sections appear as
 * data is added.
 */

import { PersonaAvatarUploader } from '@/components/persona-configurator/PersonaAvatarUploader'
import type { PersonaData, PersonaType, JtbdData } from '@ispot/shared'

interface Props {
  personaData: PersonaData
  personaType: PersonaType
  onPhotoChange?: (photo: string | undefined) => void
}

const severityDot = (severity?: string) =>
  severity === 'high' ? 'bg-error' : severity === 'medium' ? 'bg-solution' : 'bg-warm-400'
const severityLabel = (severity?: string) =>
  severity === 'high' ? 'Critical' : severity === 'medium' ? 'Moderate' : 'Minor'
const importanceDot = (importance?: string) =>
  importance === 'must_have' ? 'bg-market' : importance === 'expected' ? 'bg-solution' : 'bg-warm-400'
const importanceLabel = (importance?: string) =>
  importance === 'must_have' ? 'Critical' : importance === 'expected' ? 'Important' : 'Nice to have'

export function PersonaCardPreview({ personaData, personaType, onPhotoChange }: Props) {
  const initials = personaData.name
    ? personaData.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  const isBuyer = personaType === 'buyer'
  const primaryJtbd: JtbdData | undefined = personaData.jtbds?.[0]
  const hasJtbd = !!primaryJtbd?.title
  const hasPains = (primaryJtbd?.pains?.length || 0) > 0
  const hasGains = (primaryJtbd?.gains?.length || 0) > 0

  // Legacy fallback for old sessions without JTBD
  const legacyGoals = personaData.goals || []
  const legacyPains = personaData.pains || []
  const legacyTools = personaData.tools || []
  const hasLegacyData = legacyGoals.length > 0 || legacyPains.length > 0 || legacyTools.length > 0
  const hasAnyData = hasJtbd || hasPains || hasGains || hasLegacyData

  return (
    <div className="w-full">

      {/* Identity row */}
      <header className="mb-5 grid grid-cols-[64px_1fr_max-content] items-center gap-4">
        {onPhotoChange ? (
          <PersonaAvatarUploader
            photo={personaData.photo}
            initials={initials}
            isBuyer={isBuyer}
            name={personaData.name}
            onChange={onPhotoChange}
          />
        ) : (
          <div className={`flex h-16 w-16 items-center justify-center overflow-hidden rounded-full font-display text-xl font-semibold text-white ${
            personaData.photo ? 'bg-warm-200' : isBuyer ? 'bg-business' : 'bg-market'
          }`}>
            {personaData.photo
              ? <img src={personaData.photo} alt="" className="h-full w-full object-cover" />
              : initials}
          </div>
        )}

        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
            {isBuyer ? 'Buyer' : 'User'} persona
          </div>
          <h2 className="mt-1 truncate font-display text-[22px] font-semibold leading-tight tracking-tight">
            {personaData.name || 'Your persona'}
          </h2>
          <p className="mt-0.5 truncate text-[13px] text-text-secondary">
            {personaData.role || 'Add a name and role'}
          </p>
          {personaData.archetype && (
            <p className={`mt-0.5 truncate font-display text-[12px] italic ${isBuyer ? 'text-business' : 'text-market'}`}>
              {personaData.archetype}
            </p>
          )}
        </div>

        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium leading-none ${
          isBuyer ? 'bg-business/10 text-business' : 'bg-market/10 text-market'
        }`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
          {isBuyer ? 'Buyer' : 'User'}
        </span>
      </header>

      {/* Quote */}
      {personaData.quote && (
        <p className="mb-5 max-w-[60ch] border-l-2 border-market pl-4 font-display text-[14px] italic leading-relaxed text-text">
          {personaData.quote}
        </p>
      )}

      {/* Primary JTBD */}
      {hasJtbd && (
        <section className="mb-4 border-t border-border-subtle pt-4">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
            Primary job to be done
          </div>
          <h3 className="mb-1 font-display text-[17px] font-semibold leading-tight tracking-tight">
            {primaryJtbd!.title}
          </h3>
          {primaryJtbd!.functional && primaryJtbd!.functional !== primaryJtbd!.title && (
            <p className="text-[13px] leading-relaxed text-text-secondary">
              {primaryJtbd!.functional}
            </p>
          )}
        </section>
      )}

      {/* Pains and Gains */}
      {(hasPains || hasGains) && (
        <div className={`grid gap-x-8 gap-y-4 border-t border-border-subtle pt-4 ${hasPains && hasGains ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
          {hasPains && (
            <section>
              <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
                <span>Key pains</span>
                <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{primaryJtbd!.pains.length}</span>
              </div>
              <ul className="m-0 list-none space-y-1.5 p-0">
                {primaryJtbd!.pains.map(p => (
                  <li key={p.id} className="grid grid-cols-[10px_1fr_max-content] items-baseline gap-3">
                    <span className={`mt-1 h-1.5 w-1.5 rounded-full ${severityDot(p.severity)}`} />
                    <span className="truncate text-[13px] text-text">{p.description}</span>
                    <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted">{severityLabel(p.severity)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {hasGains && (
            <section>
              <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
                <span>Desired outcomes</span>
                <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{primaryJtbd!.gains.length}</span>
              </div>
              <ul className="m-0 list-none space-y-1.5 p-0">
                {primaryJtbd!.gains.map(g => (
                  <li key={g.id} className="grid grid-cols-[10px_1fr_max-content] items-baseline gap-3">
                    <span className={`mt-1 h-1.5 w-1.5 rounded-full ${importanceDot(g.importance)}`} />
                    <span className="truncate text-[13px] text-text">{g.description}</span>
                    <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted">{importanceLabel(g.importance)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {/* Legacy data fallback for old sessions */}
      {!hasJtbd && !hasPains && !hasGains && hasLegacyData && (
        <div className="border-t border-border-subtle pt-4 space-y-4">
          {legacyGoals.length > 0 && (
            <section>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-text">Goals</div>
              <ul className="m-0 list-none space-y-1 p-0 text-[13px] text-text">
                {legacyGoals.map((g: string) => <li key={g}>· {g}</li>)}
              </ul>
            </section>
          )}
          {legacyPains.length > 0 && (
            <section>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-text">Pains</div>
              <ul className="m-0 list-none space-y-1 p-0 text-[13px] text-text">
                {legacyPains.map((p: { text: string }) => <li key={p.text}>· {p.text}</li>)}
              </ul>
            </section>
          )}
          {legacyTools.length > 0 && (
            <section>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-text">Tools</div>
              <div className="flex flex-wrap gap-1">
                {legacyTools.map((t: string) => (
                  <span key={t} className="rounded-md bg-warm-100 px-2 py-0.5 text-[12px] text-text">{t}</span>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Empty state */}
      {!hasAnyData && (
        <div className="border-t border-border-subtle pt-6 text-center text-[13px] italic text-text-muted">
          Sections will appear as you add data.
        </div>
      )}

      {/* Footer summary */}
      {hasAnyData && (
        <div className="mt-5 flex items-center gap-3 border-t border-border-subtle pt-3 text-[11px] text-text-muted">
          <span className="tabular-nums">
            {hasJtbd && <><span className="font-medium text-text">1</span> JTBD</>}
            {hasPains && <> · <span className="font-medium text-text">{primaryJtbd!.pains.length}</span> pains</>}
            {hasGains && <> · <span className="font-medium text-text">{primaryJtbd!.gains.length}</span> outcomes</>}
          </span>
          {hasJtbd && (
            <span className="ml-auto inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-market" />
              Primary JTBD defined
            </span>
          )}
        </div>
      )}
    </div>
  )
}
